import { useEffect, useRef } from 'react';
import { useGoogleMaps } from './GoogleMapsProvider';

/**
 * Campos que se piden al elegir una sugerencia. Google cobra la consulta según
 * el campo más caro: `displayName` es Pro y el resto Essentials, así que quien
 * no lo usa tiene que pasar su propia lista por `fields`.
 */
const DEFAULT_FIELDS = ['displayName', 'formattedAddress', 'location', 'addressComponents'];

/**
 * Input de dirección con sugerencias de Google Places.
 *
 * Por dentro es el web component `PlaceAutocompleteElement` (API nueva). Los
 * estilos del shadow DOM están en `styles/base.scss`, bajo
 * `.google-autocomplete-wrapper`.
 *
 * `dict` se mantiene por los formularios que ya lo pasaban; `countryCode` y
 * `center` permiten usarlo sin el diccionario completo. Sin `center` no se
 * aplica `locationBias`, que es lo que corresponde cuando las sugerencias
 * tienen que cubrir todo el país y no una ciudad.
 *
 * @param {Object} props
 * @param {Function} props.onPlaceSelect Recibe el lugar elegido (`Place.toJSON()`).
 * @param {Object} [props.dict] Diccionario del país, con `country_short_name` y `map`.
 * @param {Object} [props.autocompleteRef] Ref al elemento, para poder limpiarlo o enfocarlo.
 * @param {string} [props.id] Id del elemento. Obligatorio si hay más de uno en la página.
 * @param {string} [props.label] Texto del label. `null` para no renderizarlo.
 * @param {string} [props.labelClass] Clases del label.
 * @param {string} [props.placeholder] Placeholder del input.
 * @param {string} [props.countryCode] ISO-2 al que se limitan las sugerencias.
 * @param {{lat: number, lng: number}} [props.center] Centro del sesgo geográfico.
 * @param {string[]} [props.fields] Campos del lugar a pedir. Definirlo fuera del
 *   render: el autocomplete se recrea si cambia.
 */
const GoogleAutocompleteInput = ({
  onPlaceSelect,
  dict,
  autocompleteRef,
  id = 'place-autocomplete-input',
  label = 'Dirección',
  labelClass = 'text-grey-dark',
  placeholder = 'Ingresá una dirección',
  countryCode,
  center,
  fields = DEFAULT_FIELDS
}) => {
  const containerRef = useRef(null);
  const { isLoaded } = useGoogleMaps();
  const region = countryCode || dict?.country_short_name;
  const lat = center?.lat ?? dict?.map?.lat;
  const lng = center?.lng ?? dict?.map?.lng;
  // Clave estable para el efecto aunque el array llegue con otra identidad.
  const fieldsKey = fields.join(',');

  useEffect(() => {
    if (!isLoaded) return;

    const initAutocomplete = async () => {
      try {
        if (!window.google?.maps?.places) {
          console.error('Google Maps Places library not loaded');

          return;
        }

        if (!containerRef.current) return;

        const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement();

        placeAutocomplete.id = id;
        placeAutocomplete.setAttribute('placeholder', placeholder);
        placeAutocomplete.setAttribute('autocomplete', 'off');

        if (region) placeAutocomplete.includedRegionCodes = [region];

        if (typeof lat === 'number' && typeof lng === 'number') {
          placeAutocomplete.locationBias = {
            center: new google.maps.LatLng(lat, lng),
            radius: 50000
          };
        }

        placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
          const place = placePrediction.toPlace();

          await place.fetchFields({ fields: fieldsKey.split(',') });

          onPlaceSelect?.(place.toJSON());
        });

        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(placeAutocomplete);

        if (autocompleteRef) {
          autocompleteRef.current = placeAutocomplete;
        }
      } catch (error) {
        console.error('Error al inicializar autocomplete:', error);
      }
    };

    initAutocomplete();
  }, [isLoaded, region, lat, lng, id, placeholder, onPlaceSelect, autocompleteRef, fieldsKey]);

  const field = <div ref={containerRef} className="google-autocomplete-wrapper"></div>;

  if (!label) return field;

  return (
    <label className="w-full">
      <span className={labelClass}>{label}</span>
      {field}
    </label>
  );
};

export default GoogleAutocompleteInput;
